import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  Toolbar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { api } from '../../../services/authService';
import useLocalStorage from '../../../hooks/useLocalStorage';

const LeadLists = () => {
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.auth.user);
  
  // State management
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLists, setSelectedLists] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAccount, setFilterAccount] = useState('');
  const [filterCampaign, setFilterCampaign] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  
  // Menu states
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedList, setSelectedList] = useState(null);
  
  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  
  // Local storage for filters
  const [selectedAccount, setSelectedAccount] = useLocalStorage('selectedAccount', '');

  // Fetch data on component mount
  useEffect(() => {
    fetchAccounts();
    fetchLists();
  }, []);

  // Fetch campaigns when account filter changes
  useEffect(() => {
    if (filterAccount) {
      fetchCampaigns(filterAccount);
    } else {
      setCampaigns([]); // Clear campaigns when no account is selected
    }
    // Clear campaign filter when account changes
    setFilterCampaign('');
  }, [filterAccount]);

  const fetchAccounts = async () => {
    try {
      const response = await api.get('/crm/accounts/');
      // Ensure we're setting an array, handle different response structures
      const accountsData = Array.isArray(response.data) ? response.data : 
                          Array.isArray(response.data.results) ? response.data.results : 
                          Array.isArray(response.data.accounts) ? response.data.accounts : [];
      setAccounts(accountsData);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setAccounts([]); // Set empty array on error
    }
  };

  const fetchCampaigns = async (accountId) => {
    try {
      const response = await api.get(`/crm/campaigns/?account=${accountId}`);
      // Ensure we're setting an array, handle different response structures
      const campaignsData = Array.isArray(response.data) ? response.data : 
                           Array.isArray(response.data.results) ? response.data.results : 
                           Array.isArray(response.data.campaigns) ? response.data.campaigns : [];
      setCampaigns(campaignsData);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setCampaigns([]); // Set empty array on error
    }
  };

  const fetchLists = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterAccount) params.append('account', filterAccount);
      if (filterCampaign) params.append('campaign', filterCampaign);
      if (filterType) params.append('list_type', filterType);
      if (filterStatus) params.append('status', filterStatus);
      
      const response = await api.get(`/crm/lists/?${params.toString()}`);
      setLists(response.data);
    } catch (error) {
      console.error('Error fetching lists:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle search and filter changes
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchLists();
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, filterAccount, filterCampaign, filterType, filterStatus]);

  const handleMenuClick = (event, list) => {
    setAnchorEl(event.currentTarget);
    setSelectedList(list);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedList(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    navigate(`/crm/lists/${selectedList.id}/edit`);
  };

  const handleView = () => {
    handleMenuClose();
    navigate(`/crm/lists/${selectedList.id}`);
  };

  const handleArchive = async () => {
    handleMenuClose();
    try {
      await api.patch(`/crm/lists/${selectedList.id}/`, { status: 'archived' });
      fetchLists();
    } catch (error) {
      console.error('Error archiving list:', error);
    }
  };

  const handleDelete = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/crm/lists/${selectedList.id}/`);
      setDeleteDialogOpen(false);
      fetchLists();
    } catch (error) {
      console.error('Error deleting list:', error);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedLists(lists.map(list => list.id));
    } else {
      setSelectedLists([]);
    }
  };

  const handleSelectList = (listId) => {
    setSelectedLists(prev => 
      prev.includes(listId) 
        ? prev.filter(id => id !== listId)
        : [...prev, listId]
    );
  };

  const handleBulkAction = (action) => {
    setBulkAction(action);
    setBulkActionDialogOpen(true);
  };

  const confirmBulkAction = async () => {
    try {
      await api.post('/crm/lists/bulk_action/', {
        list_ids: selectedLists,
        action: bulkAction
      });
      setBulkActionDialogOpen(false);
      setSelectedLists([]);
      fetchLists();
    } catch (error) {
      console.error('Error performing bulk action:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'draft': return 'warning';
      case 'archived': return 'default';
      default: return 'default';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'manual': return 'Manual';
      case 'dynamic': return 'Dynamic';
      case 'filtered': return 'Filtered';
      default: return type;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link color="inherit" href="/crm/dashboard">
            Dashboard
          </Link>
          <Typography color="text.primary">Lead Lists</Typography>
        </Breadcrumbs>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1" color="text.primary">
            Lead Lists
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/crm/lists/create')}
          >
            Create List
          </Button>
        </Box>
      </Box>

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search lists..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Account</InputLabel>
                <Select
                  value={filterAccount}
                  onChange={(e) => setFilterAccount(e.target.value)}
                  label="Account"
                >
                  <MenuItem value="">All Accounts</MenuItem>
                  {(Array.isArray(accounts) ? accounts : []).map(account => (
                    <MenuItem key={account.id} value={account.id}>
                      {account.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Campaign</InputLabel>
                <Select
                  value={filterCampaign}
                  onChange={(e) => setFilterCampaign(e.target.value)}
                  label="Campaign"
                  disabled={!filterAccount}
                >
                  <MenuItem value="">All Campaigns</MenuItem>
                  {(Array.isArray(campaigns) ? campaigns : []).map(campaign => (
                    <MenuItem key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  label="Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="manual">Manual</MenuItem>
                  <MenuItem value="dynamic">Dynamic</MenuItem>
                  <MenuItem value="filtered">Filtered</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={1}>
              <Tooltip title="Refresh">
                <IconButton onClick={fetchLists} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Bulk Actions Toolbar */}
      {selectedLists.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <Toolbar>
            <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
              Selected: {selectedLists.length} lists
            </Typography>
            <Button
              startIcon={<ArchiveIcon />}
              onClick={() => handleBulkAction('archive')}
              sx={{ mr: 1 }}
            >
              Archive
            </Button>
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => handleBulkAction('activate')}
              sx={{ mr: 1 }}
            >
              Activate
            </Button>
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => handleBulkAction('refresh')}
              sx={{ mr: 1 }}
            >
              Refresh
            </Button>
            <Button
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => handleBulkAction('delete')}
            >
              Delete
            </Button>
          </Toolbar>
        </Card>
      )}

      {/* Lists Table */}
      <Card>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={lists.length > 0 && selectedLists.length === lists.length}
                    indeterminate={selectedLists.length > 0 && selectedLists.length < lists.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Leads</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : lists.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No lists found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                lists.map((list) => (
                  <TableRow key={list.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedLists.includes(list.id)}
                        onChange={() => handleSelectList(list.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ cursor: 'pointer' }} onClick={() => navigate(`/crm/lists/${list.id}`)}>
                        {list.name}
                      </Typography>
                      {list.description && (
                        <Typography variant="body2" color="text.secondary">
                          {list.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={getTypeLabel(list.list_type)} size="small" />
                    </TableCell>
                    <TableCell>{list.total_leads || 0}</TableCell>
                    <TableCell>
                      <Chip 
                        label={list.status} 
                        color={getStatusColor(list.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(list.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {new Date(list.updated_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={(e) => handleMenuClick(e, list)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleView}>
          <ViewIcon sx={{ mr: 1 }} />
          View
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleArchive}>
          <ArchiveIcon sx={{ mr: 1 }} />
          Archive
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete List</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedList?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Action Confirmation Dialog */}
      <Dialog open={bulkActionDialogOpen} onClose={() => setBulkActionDialogOpen(false)}>
        <DialogTitle>
          {bulkAction === 'delete' ? 'Delete Lists' : 'Update Lists'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {bulkAction === 'delete' 
              ? `Are you sure you want to delete ${selectedLists.length} lists? This action cannot be undone.`
              : bulkAction === 'refresh'
              ? `Are you sure you want to refresh ${selectedLists.length} dynamic lists?`
              : `Are you sure you want to ${bulkAction} ${selectedLists.length} lists?`
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkActionDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={confirmBulkAction} 
            color={bulkAction === 'delete' ? 'error' : 'primary'} 
            variant="contained"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeadLists; 